var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_PRODUCT_COUNTRY from 'api/mutations/ADD_PRODUCT_COUNTRY';
import REMOVE_PRODUCT_COUNTRY from 'api/mutations/REMOVE_PRODUCT_COUNTRY';
import UPDATE_PRODUCT_COUNTRY from 'api/mutations/UPDATE_PRODUCT_COUNTRY';
import GET_PRODUCT_COUNTRIES from 'api/queries/GET_PRODUCT_COUNTRIES';
import { withAttachments } from 'utils/graphql';
const createInitialState = () => ({
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    continentFilter: null,
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
});
const mutateWithAttachments = withAttachments((options) => client.mutate(options));
export const fetchProductCountries = createAsyncThunk('productCountries/fetch', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, rejectWithValue }) {
    const state = getState();
    const { pageInfo, continentFilter } = state.parameters.productCountries;
    try {
        const response = yield client.query({
            query: GET_PRODUCT_COUNTRIES,
            variables: {
                first: 25,
                after: pageInfo.endCursor,
                continentId: continentFilter || undefined,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.autocomplete.productCountries;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addProductCountry = createAsyncThunk('productCountries/add', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ name, description, continentId, }, { rejectWithValue }) {
    try {
        const response = yield mutateWithAttachments({
            mutation: ADD_PRODUCT_COUNTRY,
            variables: { name, description, continentId: continentId || null },
            fetchPolicy: 'no-cache',
        });
        return response.data.addProductCountry.productCountry;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateProductCountry = createAsyncThunk('productCountries/update', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ id, name, description, continentId, }, { rejectWithValue }) {
    try {
        const response = yield mutateWithAttachments({
            mutation: UPDATE_PRODUCT_COUNTRY,
            variables: { productCountryId: id, name, description, continentId: continentId !== null && continentId !== void 0 ? continentId : null },
        });
        return response.data.updateProductCountry.productCountry;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeProductCountry = createAsyncThunk('productCountries/remove', (id_1, _a) => __awaiter(void 0, [id_1, _a], void 0, function* (id, { rejectWithValue }) {
    try {
        yield client.mutate({
            mutation: REMOVE_PRODUCT_COUNTRY,
            variables: { productCountryId: id },
        });
        return id;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const productCountriesSlice = createSlice({
    name: 'productCountries',
    initialState: createInitialState(),
    reducers: {
        resetProductCountries: () => createInitialState(),
        setContinentFilter(state, action) {
            state.continentFilter = action.payload;
            state.items = [];
            state.pageInfo = createInitialState().pageInfo;
            state.status.fetch = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductCountries.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchProductCountries.fulfilled, (state, action) => {
            var _a;
            const newItems = action.payload.edges.map((edge) => edge.node);
            const unique = newItems.filter((newItem) => !state.items.some((existing) => existing.id === newItem.id));
            state.items = [...state.items, ...unique];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchProductCountries.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.payload || action.error.message || null;
        })
            .addCase(addProductCountry.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addProductCountry.fulfilled, (state, action) => {
            state.items = [...state.items, action.payload].sort((a, b) => a.name.localeCompare(b.name));
            state.status.add = 'succeeded';
        })
            .addCase(addProductCountry.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.payload || action.error.message || null;
        })
            .addCase(updateProductCountry.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateProductCountry.fulfilled, (state, action) => {
            const idx = state.items.findIndex((item) => item.id === action.payload.id);
            if (idx !== -1) {
                state.items[idx] = action.payload;
            }
            state.items = [...state.items].sort((a, b) => a.name.localeCompare(b.name));
            state.status.update = 'succeeded';
        })
            .addCase(updateProductCountry.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.payload || action.error.message || null;
        })
            .addCase(removeProductCountry.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeProductCountry.fulfilled, (state, action) => {
            state.items = state.items.filter((item) => item.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeProductCountry.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload || action.error.message || null;
        });
    },
});
export const { resetProductCountries, setContinentFilter } = productCountriesSlice.actions;
export const selectProductCountries = (state) => state.parameters.productCountries.items;
export const selectProductCountryById = (state, id) => state.parameters.productCountries.items.find((c) => c.id === id);
export const selectProductCountriesPageInfo = (state) => state.parameters.productCountries.pageInfo;
export const selectProductCountriesStatus = (state) => state.parameters.productCountries.status;
export const selectProductCountriesErrors = (state) => state.parameters.productCountries.errors;
export const selectProductCountriesRefetchFlags = (state) => state.parameters.productCountries.refetchFlags;
export const selectProductCountriesContinentFilter = (state) => state.parameters.productCountries.continentFilter;
export default productCountriesSlice.reducer;
